import { ButtonGroup, Center, Flex, FlexProps, Text } from "@chakra-ui/react";
import { useMemo, useState } from "react";

import PeriodPicker, {
  Period,
  periodList,
} from "@/common/components/PeriodPicker";
import RollingTimeWindowPicker, {
  RollingWindowPeriod,
} from "@/common/components/RollingTimeWindowPicker";
import {
  SectionHeading,
  SectionHeadingProps,
} from "@/common/components/SectionHeading";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useSearchParam } from "@/common/hooks/useSearchParam";
import { periodToDates } from "@/common/utils/periodToDates";
import SatisfactionAverageTimelineChart from "@/feedback/components/SatisfactionAverageTimelineChart";
import { useFeatureFeedbackTimelineData } from "@/feedback/data/useFeedbackTimelineData";

const allowedPeriods = [
  "pastHour",
  "past7days",
  "past30days",
  "past3months",
  "past6months",
] as const;

type Props = Omit<FlexProps, "title"> & {
  featureId: string;
  subsegmentId?: string;
  title?: string | null;
  _sectionHeading?: SectionHeadingProps;
};

export function SatisfactionTimeline({
  featureId,
  subsegmentId,
  title = "Average satisfaction",
  _sectionHeading,
  ...rest
}: Props) {
  const { envId } = useEnvironment();

  const [period, setPeriod] = useSearchParam<Period>("feedbackPeriod", {
    allowlist: periodList,
    fallback: "past7days",
  });

  const [rollingWindowSize, setRollingWindowSize] =
    useState<RollingWindowPeriod>(7);

  const { startDate, endDate } = useMemo(() => periodToDates(period), [period]);

  const { data = [], isLoading } = useFeatureFeedbackTimelineData(
    {
      envId: envId!,
      startDate: startDate.toDate(),
      endDate: endDate.toDate(),
      rollingWindowSize,
      featureIds: [featureId],
      subsegment: subsegmentId,
    },
    {
      enabled: period !== "pastHour",
    },
  );

  if (period === "pastHour") {
    return (
      <Center flexDirection="column" {...rest}>
        <Text color="dimmed" fontSize="sm">
          Satisfaction timeline is not available for the past hour.
        </Text>
      </Center>
    );
  }

  return (
    <Flex direction="column" {...rest}>
      <SectionHeading
        isLoading={isLoading}
        rightElement={
          <ButtonGroup spacing={2}>
            <PeriodPicker
              allowedPeriods={allowedPeriods}
              value={period}
              onChange={(val) => setPeriod(val, { replace: true })}
            />
            <RollingTimeWindowPicker
              value={rollingWindowSize}
              onChange={(val) => setRollingWindowSize(val)}
            />
          </ButtonGroup>
        }
        {..._sectionHeading}
      >
        {title}
      </SectionHeading>
      <SatisfactionAverageTimelineChart
        data={data}
        height={200}
        isLoading={isLoading}
      />
    </Flex>
  );
}
